import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, Search, ShoppingBag, User, LogOut, LayoutDashboard, Network, Heart } from "lucide-react";
import { useAuth, useCart, useWishlist } from "../context/Store";
import { Button } from "../components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
  { to: "/partner", label: "Partner Program" },
];

const LOGO_MARK = "/assets/vearn-logo-transparent.png";
const LOGO_LIGHT = "/assets/vearn-logo-light.png";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { cart } = useCart();
  const { wishlistIds } = useWishlist();
  const navigate = useNavigate();
  const location = useLocation();
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const hero = location.pathname === "/" && !scrolled;

  const submitSearch = (e) => {
    e.preventDefault();
    if (search.trim()) {
      navigate(`/shop?search=${encodeURIComponent(search.trim())}`);
      setOpen(false);
    }
  };

  const linkCls = hero
    ? "text-white/85 hover:text-white"
    : "text-brand-ink/70 hover:text-brand-blue";
  const iconCls = hero ? "text-white" : "text-brand-ink";
  const searchCls = hero
    ? "bg-white/15 text-white placeholder:text-white/50 focus:ring-brand-cyan"
    : "bg-brand-ink/5 focus:ring-brand-cyan";
  const searchIconCls = hero ? "text-white/60" : "text-brand-ink/40";

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        hero
          ? "bg-brand-navy/55 backdrop-blur-md"
          : "backdrop-blur-xl bg-white/75 border-b border-brand-ink/5"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 md:h-20 flex items-center gap-2 md:gap-4">
        <Link to="/" data-testid="nav-logo" className="shrink-0">
          <img
            src={hero ? LOGO_LIGHT : LOGO_MARK}
            alt="Vearn"
            className="h-8 md:h-11 w-auto transition-[filter] duration-500"
            style={hero ? { filter: "drop-shadow(0 0 14px rgba(0,229,255,0.55)) drop-shadow(0 0 34px rgba(41,98,255,0.35))" } : undefined}
          />
        </Link>

        <nav className="hidden md:flex items-center gap-8 ml-6">
          {NAV_LINKS.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`text-sm font-medium transition-colors ${linkCls}`}>
              {l.label}
            </Link>
          ))}
        </nav>

        <form onSubmit={submitSearch} className="hidden md:flex flex-1 max-w-md ml-auto relative">
          <Search className={`absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 ${searchIconCls}`} />
          <input
            data-testid="nav-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search products..."
            className={`w-full h-11 rounded-full pl-11 pr-4 text-sm outline-none focus:ring-2 transition-shadow ${searchCls}`}
          />
        </form>

        <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3 ml-auto md:ml-0">
          <Link to="/wishlist" data-testid="nav-wishlist-link" className={`relative hidden min-[430px]:flex p-1.5 sm:p-2 rounded-full transition-colors ${hero ? "hover:bg-white/10" : "hover:bg-brand-ink/5"}`}>
            <Heart className={`h-5 w-5 ${iconCls}`} />
            {wishlistIds.length > 0 && (
              <span data-testid="nav-wishlist-count" className="absolute -top-0.5 -right-0.5 h-5 min-w-5 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">
                {wishlistIds.length}
              </span>
            )}
          </Link>
          <Link to="/cart" data-testid="nav-cart-link" className={`relative hidden min-[430px]:flex p-1.5 sm:p-2 rounded-full transition-colors ${hero ? "hover:bg-white/10" : "hover:bg-brand-ink/5"}`}>
            <ShoppingBag className={`h-5 w-5 ${iconCls}`} />
            {cart.count > 0 && (
              <span data-testid="nav-cart-count" className="absolute -top-0.5 -right-0.5 h-5 min-w-5 px-1 rounded-full brand-gradient-bg text-white text-[10px] font-bold flex items-center justify-center">
                {cart.count}
              </span>
            )}
          </Link>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="nav-user-menu" title={user.name} className={`flex items-center gap-1 sm:gap-2 min-h-10 max-w-[150px] pl-1 pr-2 sm:pr-3 py-1 rounded-full border transition-colors ${hero ? "border-white/25 hover:border-white/50" : "border-brand-ink/10 hover:border-brand-blue/40"}`}>
                  <span className="h-8 w-8 rounded-full brand-gradient-bg text-white text-xs font-bold flex items-center justify-center">
                    {user.name?.[0]?.toUpperCase()}
                  </span>
                  <span className={`block min-w-0 max-w-[112px] whitespace-normal break-words text-[11px] leading-tight font-medium sm:max-w-24 sm:truncate sm:whitespace-nowrap sm:text-sm ${hero ? "text-white" : ""}`}>{user.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem data-testid="nav-account-link" onClick={() => navigate("/account")}>
                  <User className="h-4 w-4 mr-2" /> My Account
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="nav-wishlist-menu" onClick={() => navigate("/wishlist")}>
                  <Heart className="h-4 w-4 mr-2" /> Wishlist
                </DropdownMenuItem>
                {user.is_partner && (
                  <DropdownMenuItem data-testid="nav-partner-link" onClick={() => navigate("/partner")}>
                    <Network className="h-4 w-4 mr-2" /> Partner Dashboard
                  </DropdownMenuItem>
                )}
                {user.role === "admin" && (
                  <DropdownMenuItem data-testid="nav-admin-link" onClick={() => navigate("/admin")}>
                    <LayoutDashboard className="h-4 w-4 mr-2" /> Admin Panel
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem data-testid="nav-logout-button" onClick={async () => { await logout(); navigate("/"); }}>
                  <LogOut className="h-4 w-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/auth" data-testid="nav-login-link">
              <Button className="rounded-full brand-gradient-bg text-white border-0 h-10 px-5 text-sm font-semibold hover:opacity-90">
                Sign in
              </Button>
            </Link>
          )}

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-button" className={`md:hidden p-2 rounded-full ${hero ? "hover:bg-white/10" : "hover:bg-brand-ink/5"}`}>
                <Menu className={`h-5 w-5 ${iconCls}`} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80">
              <form onSubmit={submitSearch} className="relative mt-8 mb-6">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-ink/40" />
                <input
                  data-testid="mobile-search-input"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search products..."
                  className="w-full h-11 rounded-full bg-brand-ink/5 pl-11 pr-4 text-sm outline-none focus:ring-2 focus:ring-brand-cyan"
                />
              </form>
              <div className="flex flex-col gap-1">
                {NAV_LINKS.map((l) => (
                  <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                    className="px-4 py-3 rounded-xl text-base font-medium hover:bg-brand-ink/5">
                    {l.label}
                  </Link>
                ))}
                <Link to="/wishlist" onClick={() => setOpen(false)} data-testid="mobile-nav-wishlist"
                  className="px-4 py-3 rounded-xl text-base font-medium hover:bg-brand-ink/5">
                  Wishlist ({wishlistIds.length})
                </Link>
                <Link to="/cart" onClick={() => setOpen(false)} data-testid="mobile-nav-cart"
                  className="px-4 py-3 rounded-xl text-base font-medium hover:bg-brand-ink/5">
                  Cart ({cart.count})
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
